import { useState, useMemo, useEffect } from 'react'
import { generateClient } from 'aws-amplify/api'
import { getHRFlags, getFlagDetails } from '../graphql/queries'
import './Archive.css'

function Archive({ companyId, userEmail }) {
  const [flags, setFlags] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [departments, setDepartments] = useState([])
  const [expandedFlags, setExpandedFlags] = useState(new Set())

  const client = useMemo(() => {
    try {
      return generateClient()
    } catch (error) {
      console.error('Error initializing GraphQL client:', error)
      return null
    }
  }, [])

  useEffect(() => {
    const fetchResolvedFlags = async () => {
      if (!client || !companyId) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        const response = await client.graphql({
          query: getHRFlags,
          variables: { companyId }
        })

        if (response.errors) {
          throw new Error(response.errors[0]?.message || 'Failed to fetch flags')
        }

        // Filter for resolved flags only
        const resolvedFlags = (response.data?.getHRFlags || []).filter(
          flag => flag.status === 'resolved'
        )

        // Fetch full details (including notes) for each resolved flag
        const flagsWithDetails = await Promise.all(
          resolvedFlags.map(async (flag) => {
            try {
              const detailsResponse = await client.graphql({
                query: getFlagDetails,
                variables: { flagId: flag.flagId }
              })
              return detailsResponse.data?.getFlagDetails || flag
            } catch (err) {
              console.error(`Error fetching details for flag ${flag.flagId}:`, err)
              return flag
            }
          })
        )

        setFlags(flagsWithDetails)

        // Extract unique departments
        const uniqueDepts = [...new Set(flagsWithDetails.map(f => f.department).filter(Boolean))]
        setDepartments(uniqueDepts.sort())
      } catch (err) {
        console.error('Error fetching resolved flags:', err)
        setError(err.message || 'Failed to load archive')
      } finally {
        setLoading(false)
      }
    }

    fetchResolvedFlags()
  }, [client, companyId, userEmail])

  const filteredFlags = useMemo(() => {
    return flags.filter(flag => {
      // Search filter - check employee name
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesName = flag.userName?.toLowerCase().includes(searchLower) ||
                           flag.userEmail?.toLowerCase().includes(searchLower)
        if (!matchesName) return false
      }

      // Department filter
      if (selectedDepartment !== 'all') {
        if (flag.department !== selectedDepartment) return false
      }

      return true
    })
  }, [flags, searchTerm, selectedDepartment])

  const toggleExpand = (flagId) => {
    setExpandedFlags(prev => {
      const next = new Set(prev)
      if (next.has(flagId)) {
        next.delete(flagId)
      } else {
        next.add(flagId)
      }
      return next
    })
  }

  const exportFlag = (flag) => {
    const exportData = {
      flagId: flag.flagId,
      category: flag.category,
      severity: flag.severity,
      status: flag.status,
      summary: flag.summary,
      createdAt: flag.createdAt,
      employeeName: flag.userName || 'Anonymous',
      employeeEmail: flag.isAnonymous ? 'Anonymous' : (flag.userEmail || 'N/A'),
      department: flag.department || 'Unknown',
      notes: flag.notes?.map(note => ({
        author: note.authorName,
        authorEmail: note.authorEmail,
        content: note.content,
        createdAt: note.createdAt
      })) || [],
      statusHistory: flag.statusHistory?.map(entry => ({
        changedBy: entry.changedByName,
        changedByEmail: entry.changedBy,
        oldStatus: entry.oldStatus,
        newStatus: entry.newStatus,
        changedAt: entry.changedAt,
        notes: entry.notes
      })) || []
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `flag-${flag.flagId}-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return dateString
    }
  }

  if (loading) {
    return <div className="archive-loading">Loading archive...</div>
  }

  if (error) {
    return <div className="archive-error">Error: {error}</div>
  }

  return (
    <div className="archive-container">
      <div className="archive-wrapper">
        <div className="archive-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by employee name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-box">
          <label htmlFor="department-filter">Department:</label>
          <select
            id="department-filter"
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredFlags.length === 0 ? (
        <div className="archive-empty">
          <div className="empty-icon">📋</div>
          <h3>No resolved flags found</h3>
          <p>
            {searchTerm || selectedDepartment !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Resolved flags will appear here once issues are marked as resolved'}
          </p>
        </div>
      ) : (
        <div className="archive-list">
          {filteredFlags.map(flag => (
            <div key={flag.flagId} className="archive-flag-card">
              <div className="flag-card-header">
                <div className="flag-card-main">
                  <div className="flag-id">Flag #{flag.flagId.split('-')[0]}</div>
                  <div className="flag-category">{flag.category}</div>
                  <div className={`flag-severity severity-${flag.severity}`}>
                    {flag.severity}
                  </div>
                </div>
                <button
                  className="export-btn"
                  onClick={() => exportFlag(flag)}
                  title="Export flag data"
                >
                  📥 Export
                </button>
              </div>

              <div className="flag-card-body">
                <div className="flag-summary">
                  <strong>Summary:</strong> {flag.summary}
                </div>

                <div className="flag-meta">
                  <div className="meta-item">
                    <strong>Employee:</strong> {flag.isAnonymous ? 'Anonymous' : (flag.userName || flag.userEmail || 'Unknown')}
                  </div>
                  <div className="meta-item">
                    <strong>Department:</strong> {flag.department || 'Unknown'}
                  </div>
                  <div className="meta-item">
                    <strong>Resolved:</strong> {formatDate(flag.createdAt)}
                  </div>
                </div>

                <button
                  className="expand-btn"
                  onClick={() => toggleExpand(flag.flagId)}
                >
                  {expandedFlags.has(flag.flagId) ? '▼ Hide Details' : '▶ Show Details'}
                </button>

                {expandedFlags.has(flag.flagId) && (
                  <div className="flag-details">
                    {flag.notes && flag.notes.length > 0 && (
                      <div className="details-section">
                        <h4>Notes ({flag.notes.length})</h4>
                        <div className="notes-list">
                          {flag.notes.map(note => (
                            <div key={note.noteId} className="note-item">
                              <div className="note-header">
                                <span className="note-author">{note.authorName}</span>
                                <span className="note-date">{formatDate(note.createdAt)}</span>
                              </div>
                              <div className="note-content">{note.content}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {flag.statusHistory && flag.statusHistory.length > 0 && (
                      <div className="details-section">
                        <h4>Status History</h4>
                        <div className="history-list">
                          {flag.statusHistory.map(entry => (
                            <div key={entry.historyId} className="history-item">
                              <div className="history-header">
                                <span className="history-change">
                                  {entry.oldStatus} → {entry.newStatus}
                                </span>
                                <span className="history-date">{formatDate(entry.changedAt)}</span>
                              </div>
                              <div className="history-by">
                                Changed by: {entry.changedByName} ({entry.changedBy})
                              </div>
                              {entry.notes && (
                                <div className="history-notes">{entry.notes}</div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(!flag.notes || flag.notes.length === 0) && 
                     (!flag.statusHistory || flag.statusHistory.length === 0) && (
                      <div className="no-details">No additional details available</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  )
}

export default Archive

